import { useMemo } from 'react';

import type { GridColDef } from '@mui/x-data-grid-pro';

/**
 * Reorders columns based on a saved order preference from localStorage
 *
 * This hook takes a list of columns and reorders them according to the user's
 * saved preference. It handles cases where:
 * - The saved order is empty (returns original columns)
 * - Some columns from the saved order no longer exist (gracefully skips them)
 * - New columns have been added (appends them to the end)
 *
 * @param columns - Array of column definitions (already filtered by features/settings)
 * @param columnOrder - Array of field names in user's preferred order from localStorage
 * @returns Reordered array of column definitions
 *
 * @example
 * ```tsx
 * const filteredColumns = useFilteredColumns({ columns: allColumns, features });
 * const orderedColumns = useOrderedColumns(filteredColumns, columnOrder);
 * ```
 */
export const useOrderedColumns = (
  columns: GridColDef[],
  columnOrder: string[],
): GridColDef[] => {
  return useMemo(() => {
    // If no saved order, return original columns
    if (!columnOrder || columnOrder.length === 0) {
      return columns;
    }

    // Deduplicate columnOrder while preserving first occurrence order
    // This prevents duplicate columns if localStorage got corrupted
    const uniqueOrderedFields = Array.from(new Set(columnOrder));

    // Create a map for O(1) lookup by field name
    const columnMap = new Map(columns.map((col) => [col.field, col]));

    // Build ordered columns array based on deduplicated order
    const orderedColumns = uniqueOrderedFields
      .map((field) => columnMap.get(field))
      .filter((col): col is GridColDef => col !== undefined);

    // Add any new columns that aren't in the saved order to the end
    const orderedFields = new Set(uniqueOrderedFields);
    const newColumns = columns.filter((col) => !orderedFields.has(col.field));

    return [...orderedColumns, ...newColumns];
  }, [columns, columnOrder]);
};
